import * as React from 'react';

import type {
  EnterpriseContract_BillingType,
  EnterpriseContract_ContractLimitType,
  EnterpriseContract_ContractState,
  EnterpriseContract_ContractType,
  Maybe,
} from '__generated__/graphql-types';
import type { EnterpriseContractTag_ContractTag } from '__generated__/graphql-types-do-not-use';

import type { CurriculumItem } from 'bundles/naptimejs/resources/programCurriculums.v1';

export type CollectionContentDraft = {
  programSlug: string;
  collectionId: string;
  badgeTemplateId?: string;
  addedContent: CurriculumItem[];
  removedContent: CurriculumItem[];
};

export type Contract = {
  endedAt?: number;
  startsAt?: number;
  isReal: boolean;
  contractType: EnterpriseContract_ContractType;
  contractState?: EnterpriseContract_ContractState | null;
  limitType?: EnterpriseContract_ContractLimitType;
  billingType?: EnterpriseContract_BillingType;
  contractTag?: Maybe<
    { __typename?: 'EnterpriseContractTag_ContractTag' } & Pick<EnterpriseContractTag_ContractTag, 'contractTagName'>
  >;
};

/**
 * This context is used to share admin-dashboard specific things between pages within the app.
 *
 * For example, we currently use the legacy context in AdminDashboardPage to share feature flags.
 * This new EnterpriseAdminContext is a replacement for that legacy context.
 */
export const EnterpriseAdminContext = React.createContext<{
  enableDataTechSku?: boolean;
  enableAssignContent?: boolean;
  hasBillableContracts?: boolean;
  hasNonBillableContracts?: boolean;
  hasSkillSets?: boolean;
  contracts?: Contract[];
  adminRoleType?: string;
  collectionContentDraft?: CollectionContentDraft;
  setCollectionContentDraft: React.Dispatch<React.SetStateAction<CollectionContentDraft | undefined>>;
}>({
  setCollectionContentDraft: () => {},
});

export const EnterpriseAdminContextProvider: React.LegacyFunctionComponentWithChildren<{
  enableDataTechSku?: boolean;
  enableAssignContent?: boolean;
  hasBillableContracts?: boolean;
  hasNonBillableContracts?: boolean;
  hasSkillSets?: boolean;
  contracts?: Contract[];
  adminRoleType?: string;
}> = ({
  enableDataTechSku,
  enableAssignContent,
  hasBillableContracts,
  hasNonBillableContracts,
  hasSkillSets,
  contracts,
  adminRoleType,
  children,
}) => {
  const [collectionContentDraft, setCollectionContentDraft] = React.useState<CollectionContentDraft | undefined>(
    undefined
  );

  return (
    <EnterpriseAdminContext.Provider
      value={{
        enableDataTechSku,
        enableAssignContent,
        hasBillableContracts,
        hasNonBillableContracts,
        hasSkillSets,
        contracts,
        collectionContentDraft,
        setCollectionContentDraft,
        adminRoleType,
      }}
    >
      {children}
    </EnterpriseAdminContext.Provider>
  );
};

export const useEnterpriseAdminContext = () => React.useContext(EnterpriseAdminContext);
